import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { BrowseNavigationState } from '../types/browseNavigation';
import { browseCatalog, type BrowseItem } from '../utils/browseApi';

interface BrowseNavigationResult {
  readonly prevId: string | null;
  readonly nextId: string | null;
  readonly positionLabel: string | null;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  /** True while a cross-page boundary fetch is in flight. */
  readonly isFetching: boolean;
  /** Whether navigating to the next item is possible (same-page or next page). */
  readonly canNext: boolean;
  /** Whether navigating to the previous item is possible (same-page or prev page). */
  readonly canPrev: boolean;
}

/**
 * Options for useBrowseNavigation.
 *
 * buildItemRoute (GL#24): per-item route resolver. When provided alongside
 * BrowseNavigationState.itemOwnerIds, the hook calls this for every
 * prev/next target instead of using the single browseNav.routePrefix.
 * This prevents 404s in union-search mode where the result set mixes own
 * items (authed route) with others'-public items (public route).
 *
 * Pass `buildArtworkDetailLink`-derived logic here from the consuming component.
 * Omit for single-catalog callers (no-op; backward-compatible).
 */
export interface UseBrowseNavigationOptions {
  buildItemRoute?: (ownerUserId: string, itemId: string) => { to: string; routePrefix: string };
}

/**
 * Reads BrowseNavigationState from React Router location.state and provides
 * prev/next navigation for stepping through catalog items on the detail page.
 * Returns null-ish values when no navigation state is available (direct URL access).
 *
 * Cross-page boundary navigation (GL#39): when the current item is the last on
 * its page (or first, for prev) and more pages exist, fetches the adjacent page
 * via browseCatalog and navigates to that page's first (or last) item.
 * Requires `query` and `pageSize` to be present in nav state (set by MyObjects).
 * Un-upgraded callers (PublicCatalog, Search — Phase 4) omit query, so boundary
 * fetch is skipped and they keep today's single-page behavior (no regression).
 */
export function useBrowseNavigation(options: UseBrowseNavigationOptions = {}): BrowseNavigationResult {
  const { buildItemRoute } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const [isFetching, setIsFetching] = useState(false);
  const fetchingRef = useRef(false); // synchronous guard for re-entry (state update is async)
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const browseNav = location.state as BrowseNavigationState | null;

  const { prevId, nextId, positionLabel } = useMemo(() => {
    if (!browseNav?.itemIds || browseNav.currentIndex == null) {
      return { prevId: null, nextId: null, positionLabel: null };
    }
    const { itemIds, currentIndex } = browseNav;
    const globalPosition = browseNav.total != null
      ? `${(browseNav.pageOffset ?? 0) + currentIndex + 1} of ${browseNav.total}`
      : `${currentIndex + 1} of ${itemIds.length}`;
    return {
      prevId: currentIndex > 0 ? itemIds[currentIndex - 1] : null,
      nextId: currentIndex < itemIds.length - 1 ? itemIds[currentIndex + 1] : null,
      positionLabel: globalPosition,
    };
  }, [browseNav]);

  // Cross-page boundary flags.
  // IMPORTANT: use browseNav.pageSize (not itemIds.length) for the boundary check.
  // MyObjects client-filters retired items (CLEARED/DELETED/DUPLICATE) so
  // itemIds.length can be < pageSize mid-catalog, which would cause a spurious
  // fetch when the user is actually at the catalog end.
  const hasMoreNext = useMemo(() => {
    if (browseNav?.total == null || browseNav.pageSize == null) return false;
    return (browseNav.pageOffset ?? 0) + browseNav.pageSize < browseNav.total;
  }, [browseNav]);

  const hasMorePrev = useMemo(() => {
    return (browseNav?.pageOffset ?? 0) > 0;
  }, [browseNav]);

  const canNext = nextId != null || hasMoreNext;
  const canPrev = prevId != null || hasMorePrev;

  const navigateToItem = useCallback(
    (targetId: string, targetIndex: number) => {
      if (!browseNav) return;
      // GL#24: resolve per-item route when itemOwnerIds + buildItemRoute are
      // available (union search). Otherwise fall back to the shared routePrefix
      // (single-catalog browse, backward compatible).
      const ownerUserId = browseNav.itemOwnerIds?.[targetIndex];
      const itemLink =
        ownerUserId && buildItemRoute
          ? buildItemRoute(ownerUserId, targetId)
          : { to: `${browseNav.routePrefix}/${targetId}`, routePrefix: browseNav.routePrefix };
      navigate(itemLink.to, {
        state: {
          itemIds: browseNav.itemIds,
          currentIndex: targetIndex,
          routePrefix: itemLink.routePrefix,
          total: browseNav.total,
          pageOffset: browseNav.pageOffset,
          browsePage: browseNav.browsePage,
          pageSize: browseNav.pageSize,
          query: browseNav.query,
          itemOwnerIds: browseNav.itemOwnerIds,
        },
      });
    },
    [browseNav, navigate, buildItemRoute],
  );

  const onNext = useCallback(() => {
    if (nextId && browseNav) {
      // Same-page next: synchronous navigation
      navigateToItem(nextId, browseNav.currentIndex + 1);
      return;
    }
    // Boundary fetch: next page
    if (!hasMoreNext || !browseNav?.query || !browseNav.pageSize || fetchingRef.current) return;
    const { query, pageSize, browsePage = 0, routePrefix, total } = browseNav;
    // Drop filterItemIds (nav-state-only) and map it to browseCatalog's itemIds.
    const { filterItemIds, ...browseQuery } = query;
    const pageOffset = browseNav.pageOffset ?? 0;
    fetchingRef.current = true;
    setIsFetching(true);
    browseCatalog({
      ...browseQuery,
      itemIds: filterItemIds ? [...filterItemIds] : undefined,
      size: pageSize,
      from: (browsePage + 1) * pageSize,
    })
      .then(response => {
        if (!mountedRef.current) return;
        // The fetched page is used as-is. Retired items (CLEARED/DELETED/DUPLICATE)
        // are always shown as dimmed placeholders in the grid, so this boundary
        // fetch and the grid agree — cross-page nav cannot land on an item the
        // grid hides.
        const newIds = response.items.map((i: BrowseItem) => i.catalogItemId);
        if (newIds.length === 0) return; // no-op: empty page
        // GL#24: carry per-item owner IDs for union search so the NEXT detail
        // page can also resolve per-item routes for its own prev/next nav.
        const newOwnerIds = response.items.map((i: BrowseItem) => i.userId ?? '');
        const firstItem = response.items[0];
        const firstOwner = firstItem?.userId ?? '';
        const firstLink =
          firstOwner && buildItemRoute
            ? buildItemRoute(firstOwner, newIds[0])
            : { to: `${routePrefix}/${newIds[0]}`, routePrefix };
        navigate(firstLink.to, {
          state: {
            itemIds: newIds,
            currentIndex: 0,
            routePrefix: firstLink.routePrefix,
            total,
            pageOffset: pageOffset + pageSize,
            browsePage: browsePage + 1,
            pageSize,
            query,
            itemOwnerIds: newOwnerIds,
          },
        });
      })
      .catch(() => {
        // fetch error: do not navigate; isFetching will be cleared in finally
      })
      .finally(() => {
        fetchingRef.current = false;
        if (mountedRef.current) setIsFetching(false);
      });
  }, [nextId, browseNav, navigateToItem, hasMoreNext, navigate, buildItemRoute]);

  const onPrev = useCallback(() => {
    if (prevId && browseNav) {
      // Same-page prev: synchronous navigation
      navigateToItem(prevId, browseNav.currentIndex - 1);
      return;
    }
    // Boundary fetch: previous page
    if (!hasMorePrev || !browseNav?.query || !browseNav.pageSize || fetchingRef.current) return;
    const { query, pageSize, browsePage = 0, routePrefix, total } = browseNav;
    // Drop filterItemIds (nav-state-only) and map it to browseCatalog's itemIds.
    const { filterItemIds, ...browseQuery } = query;
    const pageOffset = browseNav.pageOffset ?? 0;
    fetchingRef.current = true;
    setIsFetching(true);
    browseCatalog({
      ...browseQuery,
      itemIds: filterItemIds ? [...filterItemIds] : undefined,
      size: pageSize,
      from: (browsePage - 1) * pageSize,
    })
      .then(response => {
        if (!mountedRef.current) return;
        const newIds = response.items.map((i: BrowseItem) => i.catalogItemId);
        if (newIds.length === 0) return; // no-op: empty page
        // GL#24: carry per-item owner IDs for union search.
        const newOwnerIds = response.items.map((i: BrowseItem) => i.userId ?? '');
        const lastItem = response.items[response.items.length - 1];
        const lastOwner = lastItem?.userId ?? '';
        const lastId = newIds[newIds.length - 1];
        const lastLink =
          lastOwner && buildItemRoute
            ? buildItemRoute(lastOwner, lastId)
            : { to: `${routePrefix}/${lastId}`, routePrefix };
        navigate(lastLink.to, {
          state: {
            itemIds: newIds,
            currentIndex: newIds.length - 1,
            routePrefix: lastLink.routePrefix,
            total,
            pageOffset: pageOffset - pageSize,
            browsePage: browsePage - 1,
            pageSize,
            query,
            itemOwnerIds: newOwnerIds,
          },
        });
      })
      .catch(() => {
        // fetch error: do not navigate
      })
      .finally(() => {
        fetchingRef.current = false;
        if (mountedRef.current) setIsFetching(false);
      });
  }, [prevId, browseNav, navigateToItem, hasMorePrev, navigate, buildItemRoute]);

  useEffect(() => {
    if (!browseNav) return;
    // Re-entry while a boundary fetch is in flight is guarded inside onPrev/onNext
    // (fetchingRef), so the keyboard handler stays registered throughout the fetch.

    const handler = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable) return;
      if (document.querySelector('[role="dialog"]')) return;

      if (e.key === 'ArrowLeft') {
        onPrev();
      } else if (e.key === 'ArrowRight') {
        onNext();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [browseNav, onPrev, onNext]);

  return { prevId, nextId, positionLabel, onPrev, onNext, isFetching, canNext, canPrev };
}
